import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/global.css';
import '../styles/Landing.css';
import { getCountryPricing, PricingConfig, PRICING } from '../utils/pricing';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('revealed'); }),
      { threshold: 0.1 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const TICKER_ITEMS = [
  'One Link', '✦', 'All Your Content', '✦', 'Free Forever', '✦',
  '60 Second Setup', '✦', 'Password Links', '✦', 'Email Collection', '✦',
  'Custom Themes', '✦', 'Click Analytics', '✦', 'No Credit Card', '✦',
];

const DEMO_LINKS = [
  { emoji: '🎨', title: 'Portfolio',        sub: 'codebytein.design',   protected: false },
  { emoji: '📸', title: 'Instagram',        sub: '@codeBytein',         protected: false },
  { emoji: '☕', title: 'Buy Me a Coffee',  sub: 'buymeacoffee.com',    protected: false },
  { emoji: '🔒', title: 'Exclusive Content', sub: 'Password protected', protected: true  },
];

const LiveCard = () => {
  const [activeLink, setActiveLink] = useState<number | null>(null);
  const [clicks, setClicks] = useState([0, 0, 0, 0]);
  const [subscribed, setSubscribed] = useState(false);

  const handleClick = (i: number) => {
    if (DEMO_LINKS[i].protected) return;
    setActiveLink(i);
    setClicks(c => c.map((v, idx) => idx === i ? v + 1 : v));
    setTimeout(() => setActiveLink(null), 500);
  };

  return (
    <div className="hero-card">
      <div className="card-profile">
        <div className="card-avatar">V</div>
        <div>
          <div className="card-name">Code Bytein</div>
          <div className="card-username">mylinx.in/code_bytein</div>
        </div>
        <div className="card-live-badge">● LIVE</div>
      </div>

      {DEMO_LINKS.map((l, i) => (
        <button
          key={i}
          className={`pill-link ${activeLink === i ? 'pill-clicked' : ''}`}
          onClick={() => handleClick(i)}
          style={{ opacity: l.protected ? 0.7 : 1 }}
        >
          <div className="pill-left">
            <span className="pill-emoji">{l.emoji}</span>
            <div>
              <div className="pill-title">{l.protected ? '🔒 ' : ''}{l.title}</div>
              <div className="pill-sub">{l.sub}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {clicks[i] > 0 && <span className="pill-click-count">+{clicks[i]}</span>}
            <span className="pill-arrow">↗</span>
          </div>
        </button>
      ))}

      <div className="card-subscribe-demo">
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--ink)' }}>📧 Stay updated</div>
        {subscribed ? (
          <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600, textAlign: 'center', padding: '6px 0' }}>✅ Subscribed!</div>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <div className="card-input-mock" />
            <div className="card-btn-mock" onClick={() => setSubscribed(true)} style={{ cursor: 'pointer' }}>Sub</div>
          </div>
        )}
      </div>

      <div className="card-stats-row">
        <div className="card-stat"><span>247</span><small>VIEWS</small></div>
        <div className="card-stat"><span>{89 + clicks.reduce((a, b) => a + b, 0)}</span><small>CLICKS</small></div>
        <div className="card-stat"><span>{12 + (subscribed ? 1 : 0)}</span><small>SUBS</small></div>
      </div>

      <div className="card-footer">POWERED BY mylinx</div>
    </div>
  );
};

const TESTIMONIALS = [
  { text: "Switched from Linktree. Never going back. Analytics alone are worth it — I can see which content my audience actually clicks.", name: "Rahul M.", role: "Content Creator, Delhi", avatar: "R", color: "#C0392B", metric: "3x more link clicks" },
  { text: "Password protected links changed my business. I send exclusive content to paying clients — zero extra tools needed.", name: "Sneha K.", role: "Photographer, Mumbai", avatar: "S", color: "#2980B9", metric: "Sold out 2 workshops" },
  { text: "Grew my email list by 400+ subscribers just from my bio link. The subscribe box on my profile does all the work.", name: "Arjun T.", role: "YouTuber, Bangalore", avatar: "A", color: "#27AE60", metric: "400+ emails collected" },
];

const COMPARE_ROWS = [
  { feature: 'Unlimited links',                    mylinx: '✓ Free',    linktree: '✓ Free',   beacons: '✓ Free'  },
  { feature: 'Click analytics',                    mylinx: '✓ Free',    linktree: '✗ Paid',   beacons: '✗ Paid'  },
  { feature: 'Email collection',                   mylinx: '✓ Free',    linktree: '✓ Free',   beacons: '✗ Paid'  },
  { feature: 'Password protected links (3 free)',  mylinx: '✓ Free',    linktree: '✗ Paid',   beacons: '✗ Paid'  },
  { feature: 'Auto URL icons',                     mylinx: '✓ Free',    linktree: '✗ No',     beacons: '✗ No'    },
  { feature: 'Remove branding',                    mylinx: '⭐ ₹49/mo', linktree: '✗ $6/mo',  beacons: '✗ Paid'  },
  { feature: 'Custom themes & colors',             mylinx: '⭐ PRO',    linktree: '✗ $6/mo',  beacons: '✗ Paid'  },
  { feature: 'Location analytics',                 mylinx: '⭐ PRO',    linktree: '✗ $15/mo', beacons: '✗ Paid'  },
  { feature: 'No ads, ever',                       mylinx: '✓ Always',  linktree: '✓ Always', beacons: '✓ Always'},
];

const FEATURES = [
  { icon: '↗',  title: 'Instant Go-Live',          desc: 'Your page is live the moment you register. Share immediately.' },
  { icon: '⤢',  title: 'Drag & Drop Reorder',      desc: 'Reorder your links exactly how you want. Smooth and instant.' },
  { icon: '◉',  title: 'Click Analytics',           desc: 'See profile views, link clicks, devices and locations.' },
  { icon: '🔒', title: 'Password Protected Links',  desc: 'Lock any link behind a password. Perfect for exclusive content.' },
  { icon: '📧', title: 'Email Collection',          desc: 'Collect subscriber emails directly from your profile page.' },
  { icon: '🎨', title: 'Custom Themes',             desc: '6 beautiful themes + fully custom colors, fonts, backgrounds.' },
  { icon: '🗓', title: 'Link Scheduling',           desc: 'Auto show/hide links at specific times. Perfect for launches.' },
  { icon: '◫',  title: 'Mobile First',              desc: 'Stunning on every screen. Designed mobile-first.' },
  { icon: '◈',  title: 'Always Free',               desc: 'Core features free forever. No bait and switch.' },
];

const Landing = () => {
  useReveal();
  const [pricing, setPricing] = useState<PricingConfig>(PRICING['IN']);
  useEffect(() => { getCountryPricing().then(setPricing); }, []);

  return (
    <div className="landing-bg">

      {/* BG ORBS */}
      <div className="landing-orbs">
        <div className="orb1" />
        <div className="orb2" />
        <div className="orb3" />
        {[...Array(6)].map((_, i) => (
          <div key={i} className="grid-line" style={{ left: `${i * 20}%`, top: 0, width: 1, height: '100%' }} />
        ))}
      </div>

      {/* NAV */}
      <nav className="navbar">
        <div className="logo">my<em>linx</em></div>
        <div className="nav-links">
          <a href="#how"      className="nav-a">How it works</a>
          <a href="#features" className="nav-a">Features</a>
          <a href="#compare"  className="nav-a">Compare</a>
          <Link to="/login"   className="nav-a">Login</Link>
          <Link to="/register" className="btn-main" style={{ padding: '10px 22px', fontSize: 12 }}>
            Get Started ↗
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero-section">
        <div className="hero-left">

          <div className="tag s1" style={{ marginBottom: 28 }}>
            <span className="tag-dot" />
            Free — No credit card needed
          </div>

          <h1 className="hero-title s2">
            One link.<br />
            <em>Infinite</em><br />
            possibilities.
          </h1>

          <p className="hero-sub s3">
            Share your portfolio, socials, store & more from one beautiful page.
            Setup in 60 seconds. No code required.
          </p>

          {/* Social proof avatars */}
          <div className="s3 avatar-proof">
            <div style={{ display: 'flex' }}>
              {['V','R','A','S','K'].map((l, i) => (
                <div key={i} className="avatar-chip"
                  style={{ background: `hsl(${i * 40 + 10}, 60%, 45%)`, marginLeft: i > 0 ? -10 : 0, zIndex: 5 - i }}>
                  {l}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              <strong style={{ color: 'var(--ink)' }}>500+ creators</strong> already using mylinx
            </div>
          </div>

          <div className="hero-btns s4">
            <Link to="/register" className="btn-main btn-pulse">Create your page ↗</Link>
            <Link to="/code_bytein" className="btn-ghost">See an example →</Link>
          </div>

          <div className="trust-badges s4">
            <span className="trust-badge">✓ No credit card</span>
            <span className="trust-badge">✓ Setup in 60s</span>
            <span className="trust-badge">✓ Free forever</span>
          </div>
        </div>

        <div className="hero-card-wrap">
          <div className="hero-card-glow" />
          <div className="hero-card-label">Interactive demo — try clicking! ↓</div>
          <LiveCard />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {[...Array(2)].map((_, j) => (
            <div key={j} className="marquee-inner">
              {TICKER_ITEMS.map((t, i) => (
                <span key={i} className={i % 2 === 1 ? 'marquee-dot' : 'marquee-text'}>{t}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="stats-section">
        <div className="glow-line" />
        <div className="stats-grid">
          {[
            { num: '500+', label: 'Active Creators', desc: 'And growing daily',  accent: false },
            { num: '60s',  label: 'Setup time',       desc: 'Fastest in class',  accent: true  },
            { num: '$0',   label: 'To get started',   desc: 'Free. Always.',     accent: false },
          ].map((s, i) => (
            <div key={i} className={`stat-item reveal ${i < 2 ? 'stat-border' : ''} ${i === 1 ? 'stat-center' : i === 2 ? 'stat-right' : ''}`}>
              <div className={`stat-num ${s.accent ? 'stat-num-accent' : ''}`}>{s.num}</div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-desc">{s.desc}</div>
            </div>
          ))}
        </div>
        <div className="glow-line" style={{ marginTop: 60 }} />
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="how-section">
        <div className="how-header reveal">
          <div className="tag" style={{ marginBottom: 20 }}>How it works</div>
          <h2 className="how-title">Three steps. <em>That's it.</em></h2>
          <p className="how-sub">Average setup time is under 90 seconds. We timed it.</p>
        </div>
        <div className="how-grid">
          {[
            { num: '01', icon: '👤', title: 'Create account',   desc: 'Sign up instantly. Claim your unique username — this becomes your permanent link forever.' },
            { num: '02', icon: '🔗', title: 'Add your links',   desc: 'Paste any URL. Portfolio, Instagram, shop, anything. Icons auto-detected. Drag to reorder.' },
            { num: '03', icon: '🚀', title: 'Share everywhere', desc: 'One link in every bio. Instagram, Twitter, YouTube — all pointing to your mylinx page.' },
          ].map((step, i) => (
            <div key={i} className="step-card reveal">
              <div className="step-num">{step.num}</div>
              <div className="step-icon-big">{step.icon}</div>
              <div className="step-tag">{step.num}</div>
              <div className="step-title">{step.title}</div>
              <div className="step-desc">{step.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="feat-section">
        <div className="feat-inner">
          <div className="feat-header reveal">
            <h2 className="feat-title">Built for<br /><em>creators.</em></h2>
            <div className="feat-count">09 FEATURES</div>
          </div>
          <div className="feat-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="feat-card reveal" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="feat-icon">{f.icon}</div>
                <div className="feat-name">{f.title}</div>
                <div className="feat-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section id="compare" className="compare-section">
        <div className="compare-inner">
          <div className="tag reveal" style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>Why mylinx?</div>
          <h2 className="compare-title reveal">mylinx vs <em>the rest</em></h2>
          <p className="compare-sub reveal">We give you more. For free. No upsells, no gotchas.</p>

          <div className="compare-table reveal">
            <div className="compare-row compare-head">
              <div className="compare-feature">Feature</div>
              <div className="compare-col mylinx-col">mylinx<br /><small style={{ fontWeight: 400, color: '#22c55e' }}>{pricing.monthlyDisplay}/mo</small></div>
              <div className="compare-col">Linktree<br /><small style={{ fontWeight: 400 }}>$9/mo</small></div>
              <div className="compare-col">Beacons<br /><small style={{ fontWeight: 400 }}>$10/mo</small></div>
            </div>
            {COMPARE_ROWS.map((row, i) => (
              <div key={i} className={`compare-row ${i % 2 === 0 ? 'compare-row-alt' : ''}`}>
                <div className="compare-feature">{row.feature}</div>
                <div className="compare-col mylinx-col">
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    color: row.mylinx.startsWith('✓') ? '#22c55e' : row.mylinx.startsWith('⭐') ? '#f59e0b' : '#ef4444'
                  }}>{row.mylinx}</span>
                </div>
                <div className="compare-col">
                  <span style={{ fontSize: 12, color: row.linktree.startsWith('✓') ? '#6b7280' : '#ef4444' }}>{row.linktree}</span>
                </div>
                <div className="compare-col">
                  <span style={{ fontSize: 12, color: row.beacons.startsWith('✓') ? '#6b7280' : '#ef4444' }}>{row.beacons}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="compare-cta reveal">
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>
              Still thinking? Your competitors already have a mylinx page.
            </p>
            <Link to="/register" className="btn-main">Claim your free page ↗</Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="proof-section">
        <div className="proof-inner">
          <div className="tag reveal" style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
            Loved by creators
          </div>
          <h2 className="proof-title reveal">Real people. <em>Real results.</em></h2>
          <div className="proof-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="proof-card reveal">
                <div className="proof-metric">{t.metric}</div>
                <div className="proof-stars">★★★★★</div>
                <p className="proof-text">"{t.text}"</p>
                <div className="proof-author">
                  <div className="proof-avatar" style={{ background: t.color }}>{t.avatar}</div>
                  <div>
                    <div className="proof-name">{t.name}</div>
                    <div className="proof-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta-section">
        <div className="cta-box reveal">
          <div className="cta-glow" />
          <div className="tag" style={{ marginBottom: 28 }}>
            <span className="tag-dot" />
            Start today — it's free
          </div>
          <h2 className="cta-title">Your audience<br />is <em>waiting.</em></h2>
          <p className="cta-sub">
            Join 500+ creators who use mylinx<br />to share everything in one place.
          </p>
          <Link to="/register" className="btn-main btn-pulse cta-btn">
            Claim your free page ↗
          </Link>
          <div className="cta-badges">
            <span>✓ No credit card</span>
            <span>✓ Setup in 60s</span>
            <span>✓ Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="logo">my<em>linx</em></div>
        <a
          href="https://nicklaunches.com/products/mylinx/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="MyLinx on Nick Launches"
          style={{
            display: 'inline-block',
            margin: '16px auto',
          }}
        >
          <img
            src="https://nicklaunches.com/badges/featured.png"
            alt="MyLinx on Nick Launches"
            width="244"
            height="56"
            style={{
              display: 'block',
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </a>
        <a href="https://maidensail.com/startup/mylinx" rel="dofollow" ><img src="https://maidensail.com/badge/mylinx.svg" alt="Featured on Maidensail" height="44"  /></a>
        <div className="footer-copy">MADE WITH OBSESSION © 2026</div>
        <div className="footer-links">
          <Link to="/about"   className="footer-a">About</Link>
          <Link to="/contact" className="footer-a">Contact</Link>
          <Link to="/terms"   className="footer-a">Terms</Link>
          <Link to="/privacy" className="footer-a">Privacy</Link>
          <Link to="/refund"  className="footer-a">Refund</Link>
          <Link to="/login"   className="footer-a">Login</Link>
          <Link to="/register" className="footer-a accent">Get started →</Link>
        </div>
      </footer>

    </div>
  );
};

export default Landing;